import { AxiosError } from 'axios';
import LayoutContext from 'components/Layout/LayoutContext';
import { AuthContext } from 'context/authContext/AuthContext';
import { RegisterContextActions } from 'context/registerContext/RegisterContextActions';
import { SituationValues, StepIds, StepStatus } from 'helpers/enums/Assembleia';
import { getProfileByJWTRoles } from 'helpers/utils/AuthUtils';
import {
  getParsedJWTAssessorAccessToken,
  JWTAssessorJson,
} from 'helpers/utils/JWTUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { toRequest } from 'helpers/utils/RequestUtils';
import { isLastChar, onlyNumbers } from 'helpers/utils/StringUtils';
import { useRegisterContext } from 'hooks/useRegisterContext';
import { CITIES_DATA } from 'library/cities';
import {
  AssessorCadastroEventoRoutePath,
  AssessorCadastroReuniaoRoutePath,
} from 'library/Routes';
import { StateCode, StateName, STATES, STATES_DATA } from 'library/states';
import translation from 'locales/translation';
import {
  ArrowLeftIcon,
  Button,
  ButtonThemes,
  CheckCircleIcon,
  Col,
  ConfirmModal,
  EditIcon,
  Input,
  ModalSizes,
  Row,
  ScreenSize,
  Select,
  Title,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import ApiService from 'services/ApiService';
import { postLocalStep } from 'services/assessor';
import {
  AuthUserPerfilId,
  IAuthUserPerfil,
} from 'store/modules/auth/IAuthUserPerfil';
import { closeModal, openModal } from 'store/modules/ModalActions';
import { makeEditable } from '../../Cadastro';
import { LocalValidation, LocalValidationType } from './LocalValidation';
import styles from './styles.module.scss';

export interface State {
  value: StateCode;
  label: StateName;
}

const localPrefix = translation.Assessor.Evento.Cadastro.Etapas.Local;

export const LENGTH = 8;
export const HYPHEN_INDEXES = [4];

export default function Local(props: { reu?: boolean }) {
  const [validator, setValidator] = useState<LocalValidationType | null>(null);
  const [sent, setSent] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [editButtonLoader, setEditButtonLoader] = useState<boolean>(false);

  const { user } = useContext(AuthContext);
  const history = useHistory();
  const { showToast } = useContext(LayoutContext);
  const match = useRouteMatch<{ id?: string; etapa?: string }>();

  const dispatchAction = useDispatch();

  const parsedToken: JWTAssessorJson | null = getParsedJWTAssessorAccessToken(
    user!.access_token
  );
  const perfil: IAuthUserPerfil | null = parsedToken
    ? getProfileByJWTRoles(parsedToken.roles)
    : null;

  const {
    state,
    localStepSetFilter,
    localStepSetFilterOld,
    changeState,
    setStepId,
    setStepCompleted,
    setStepStatus,
  } = useRegisterContext();

  const completedStep =
    state.completed.find(
      (item: { step: StepIds }) => item.step === StepIds.local
    )?.completed && state.status !== StepStatus.EM_EDICAO;

  useEffect(() => {
    if (!match.params.id) {
      setTimeout(() => {
        if (props.reu) {
          history.push(`${AssessorCadastroReuniaoRoutePath}etapas/dados`);
        } else {
          history.push(`${AssessorCadastroEventoRoutePath}etapas/dados`);
        }
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match]);

  useEffect(() => {
    setValidator(LocalValidation(state));
  }, [state]);

  useEffect(() => {
    if (validator && validator.allIsValid()) {
      setSent(false);
    }
  }, [validator, validator?.allIsValid]);

  const sortByStateName = (states: State[]): State[] =>
    states.sort(stateAndCityComparer);

  const stateAndCityComparer = (
    itemA: State | string,
    itemB: State | string,
    cityA?: string,
    cityB?: string
  ) => {
    let labelA: string, labelB: string;

    if (typeof itemA === 'object' && typeof itemB === 'object') {
      labelA = itemA.label;
      labelB = itemB.label;
    } else if (typeof itemA === 'string' && typeof itemB === 'string') {
      labelA = itemA;
      labelB = itemB;
    } else {
      throw new Error(
        'Os argumentos devem ser do tipo { label: string } ou string.'
      );
    }

    const stateComparison = labelA.localeCompare(labelB);
    if (stateComparison !== 0) {
      return stateComparison;
    }

    if (cityA && cityB) {
      return cityA.localeCompare(cityB);
    }

    return 0;
  };

  const handleSubmit = () => {
    if (!completedStep) {
      if (!sent) setSent(true);
      if (!validator || !validator.allIsValid()) return;
      setLoading(true);

      const requestSent = {
        eventId: Number(match.params.id),
        postcode: state.local.inputs.postcode
          .slice(0, LENGTH)
          .split('')
          .reduce((acc: string, digit: string, i: number) => {
            const result = `${acc}${digit}`;

            if (!isLastChar(i, state.local.inputs.postcode)) {
              if (HYPHEN_INDEXES.indexOf(i) >= 0) return `${result}-`;
            }

            return result;
          }, ''),
        state: state.local.inputs.state,
        city: state.local.inputs.city,
        neighborhood: state.local.inputs.neighborhood,
        address: state.local.inputs.address,
        number: state.local.inputs.number,
        localReference: state.local.inputs.localReference,
      };

      if (
        JSON.stringify(state.local.inputs) ===
        JSON.stringify(state.localOld.inputs)
      ) {
        if (props.reu) {
          history.push(
            `${AssessorCadastroReuniaoRoutePath}${match.params.id}/etapas/pauta`
          );
        } else {
          history.push(
            `${AssessorCadastroEventoRoutePath}${match.params.id}/etapas/pauta`
          );
        }
      } else {
        postLocalStep(requestSent)
          .then(() => {
            setLoading(false);

            setStepCompleted(
              state.completed.map(
                (item: { step: StepIds; completed: boolean }) =>
                  item.step === StepIds.local
                    ? { ...item, completed: true }
                    : item
              )
            );

            localStepSetFilterOld(state.local.inputs);

            if (state.showAlert) {
              history.push(state.toPage);
            } else {
              if (props.reu) {
                history.push(
                  `${AssessorCadastroReuniaoRoutePath}${match.params.id}/etapas/pauta`
                );
              } else {
                history.push(
                  `${AssessorCadastroEventoRoutePath}${match.params.id}/etapas/pauta`
                );
              }
            }
          })
          .catch(() => {
            setLoading(false);
            showToast({
              pauseOnFocusLoss: true,
              theme: ToastTypes.Error,
              showStatusBar: true,
              timeout: 4000,
              label: 'Erro ao salvar a etapa',
            });
          });
      }
    } else {
      if (props.reu) {
        history.push(
          `${AssessorCadastroReuniaoRoutePath}${match.params.id}/etapas/pauta`
        );
      } else {
        history.push(
          `${AssessorCadastroEventoRoutePath}${match.params.id}/etapas/pauta`
        );
      }
    }
  };

  useEffect(() => {
    setStepId(StepIds.local);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const states = STATES.map((code) => ({
      value: code,
      label: STATES_DATA[code].name as StateName,
    }));

    changeState(
      RegisterContextActions.LOCAL_STATES_OPTIONS,
      sortByStateName(states)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.local.inputs.state]);

  useEffect(() => {
    if (state.local.inputs.state && state.local.stateOptions.length) {
      const states = state.local.stateOptions;

      const foundState: any = states.find(
        ({ label, value }: State) =>
          label === state.local.inputs.state ||
          value === state.local.inputs.state
      );

      const data = CITIES_DATA[foundState!?.value!];
      const sortable = data.sort(stateAndCityComparer);

      changeState(
        RegisterContextActions.LOCAL_CITIES_OPTIONS,
        sortable!?.map((cities: string | string[]) => {
          return {
            label: cities,
            value: cities,
          };
        })
      );
    } else {
      changeState(
        RegisterContextActions.LOCAL_CITIES_OPTIONS,
        Object.values(CITIES_DATA)
          .reduce((acc, city) => {
            return acc.concat(city);
          }, [])
          .sort(stateAndCityComparer)
          .filter((item, pos, self) => self.indexOf(item) === pos)
          .map((cities) => {
            return {
              label: cities,
              value: cities,
            };
          })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.local.inputs.state, state.local.stateOptions]);

  useEffect(() => {
    if (!completedStep) {
      if (
        state.local.inputs.postcode &&
        state.local.inputs.postcode.length > 7 &&
        state.localOld.inputs.postcode !== state.local.inputs.postcode
      ) {
        const delayDebounceFn = setTimeout(() => {
          changeState(RegisterContextActions.LOCAL_LOAD, true);
          toRequest(ApiService.get, [
            `https://viacep.com.br/ws/${state.local.inputs.postcode}/json/`,
          ])
            .then(({ data }) => {
              if (data.erro) {
                showToast({
                  pauseOnFocusLoss: true,
                  theme: ToastTypes.Warning,
                  showStatusBar: true,
                  timeout: 4000,
                  label: translation.Toasts.Errors.errLoadCEP,
                });

                localStepSetFilter({
                  state: '',
                  city: '',
                  neighborhood: '',
                  address: '',
                });
              } else {
                localStepSetFilter({
                  state: data.uf,
                  city: data.localidade,
                  neighborhood: data.bairro,
                  address: data.logradouro,
                });
              }
            })
            .catch(() => {
              showToast({
                pauseOnFocusLoss: true,
                theme: ToastTypes.Error,
                showStatusBar: true,
                timeout: 4000,
                label: translation.Toasts.Errors.errLoadCEP2,
              });

              localStepSetFilter({
                state: '',
                city: '',
                neighborhood: '',
                address: '',
              });
            })
            .finally(() =>
              changeState(RegisterContextActions.LOCAL_LOAD, false)
            );
        }, 800);

        return () => clearTimeout(delayDebounceFn);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.local.inputs.postcode]);

  useEffect(() => {
    if (
      state.showAlert &&
      (state.status === StepStatus.EM_EDICAO ||
        state.eventStatus === SituationValues.EDITABLE)
    ) {
      const mk = getUniqueKey();

      dispatchAction(
        openModal(ConfirmModal, {
          modalKey: mk,
          size: ModalSizes.Large,
          title: 'Confirmação',
          mobileOn: ScreenSize.xs,
          children:
            'Há modificações que ainda não foram salvas. Você deseja salvar essas alterações antes de alterar a etapa atual?',
          onClose: () => {
            changeState(RegisterContextActions.SET_SHOW_ALERT, false);
            dispatchAction(closeModal(mk));
          },
          onCancel: () => {
            changeState(RegisterContextActions.SET_SHOW_ALERT, false);
            history.push(state.toPage);
            localStepSetFilter(state.localOld.inputs);
            dispatchAction(closeModal(mk));
          },
          onConfirm: () => {
            changeState(RegisterContextActions.SET_SHOW_ALERT, false);
            dispatchAction(closeModal(mk));
            handleSubmit();
          },
          language: {
            confirm: 'Salvar',
            cancel: 'Não salvar',
          },
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showAlert]);

  return match.params.id ? (
    <Row className={styles.root}>
      <Row className={styles.title_content}>
        <Col cols={[12, 12, 6, 6, 6]}>
          <Row>
            <Title>{localPrefix.Titulo}</Title>
            {state.data.campos.flgEventTest ? (
              <span
                style={{
                  color: '#e60000',
                  fontSize: '12px',
                  marginTop: '12px',
                }}
              >
                {translation.Assessor.Evento.Cadastro.Etapas.Dados.EventoTeste}
              </span>
            ) : (
              <Fragment />
            )}
          </Row>
        </Col>

        {[SituationValues.AVAILABLE, SituationValues.IN_PREPARATION].includes(
          state.eventStatus!
        ) &&
        ((state.status !== StepStatus.EM_EDICAO &&
          [AuthUserPerfilId.PertencerConsulta].includes(perfil!?.id!) &&
          props.reu) ||
          ![AuthUserPerfilId.PertencerConsulta].includes(perfil!?.id!)) ? (
          <Col cols={[12, 12, 6, 6, 6]} end buttonActionsCol>
            <Button
              theme={ButtonThemes.Primary}
              leftIcon={<EditIcon />}
              onClick={() => {
                setEditButtonLoader(true);
                makeEditable(
                  String(match.params.id),
                  () => {
                    setEditButtonLoader(false);
                    setStepStatus(StepStatus.EM_EDICAO);
                    changeState(
                      RegisterContextActions.SET_EVENT_STATUS,
                      SituationValues.EDITABLE
                    );
                  },
                  (error) => {
                    setEditButtonLoader(false);
                    showToast({
                      pauseOnFocusLoss: true,
                      theme: ToastTypes.Error,
                      showStatusBar: true,
                      timeout: 4000,
                      label:
                        translation.Toasts.Errors.Keys[
                          `${
                            (error as AxiosError<Error>).response?.data
                              .message ||
                            (error as AxiosError<Error>).response?.data ||
                            (error as AxiosError<Error>).message
                          }` as keyof typeof translation.Toasts.Errors.Keys
                        ] ?? translation.Toasts.Errors.defaultAlternative,
                    });
                  }
                );
              }}
              disabled={editButtonLoader}
              loading={editButtonLoader}
            >
              {localPrefix.Buttons.Editar}
            </Button>
          </Col>
        ) : undefined}
      </Row>
      <Row>
        <Col cols={[12, 12, 4, 4, 4]}>
          <Input
            data-testid={'input-cep'}
            label={localPrefix.Campos.CEP.Label}
            placeholder={localPrefix.Campos.CEP.Placeholder}
            value={state.local.inputs.postcode
              .slice(0, LENGTH)
              .split('')
              .reduce((acc: string, digit: string, i: number) => {
                const result = `${acc}${digit}`;

                if (!isLastChar(i, state.local.inputs.postcode)) {
                  if (HYPHEN_INDEXES.indexOf(i) >= 0) return `${result}-`;
                }

                return result;
              }, '')}
            onChange={(e) => {
              localStepSetFilter({
                postcode: onlyNumbers(e.target.value),
              });
            }}
            inputMode={'numeric'}
            disabled={state.status === StepStatus.VISUALIZACAO}
          />
        </Col>
        <Col cols={[12, 12, 4, 4, 4]}>
          <Select
            data-testid={'input-estado'}
            label={localPrefix.Campos.Estado.Label}
            placeholder={localPrefix.Campos.Estado.Placeholder}
            value={state.local.inputs.state}
            options={state.local.stateOptions}
            onChange={(e) => {
              localStepSetFilter({
                state: e,
                city: undefined,
              });
            }}
            loading={state.local.loadData}
            panelFilter
            error={sent && !!validator && validator.stateIsValid().error}
            helperText={
              sent && !!validator ? validator.stateIsValid().helperText : ''
            }
            clearButton={!completedStep}
            disabled={
              state.local.loadData || state.status === StepStatus.VISUALIZACAO
            }
          />
        </Col>
        <Col cols={[12, 12, 4, 4, 4]}>
          <Select
            data-testid={'input-cidade'}
            label={localPrefix.Campos.Cidade.Label}
            placeholder={localPrefix.Campos.Cidade.Placeholder}
            value={state.local.inputs.city}
            options={state.local.citiesOptions}
            onChange={(e) => {
              localStepSetFilter({
                city: e,
              });
            }}
            disabled={
              state.local.loadData || state.status === StepStatus.VISUALIZACAO
                ? true
                : !state.local.inputs.state
            }
            loading={state.local.loadData}
            panelFilter
            error={sent && !!validator && validator.cityIsValid().error}
            helperText={
              sent && !!validator ? validator.cityIsValid().helperText : ''
            }
            clearButton={!completedStep}
          />
        </Col>
        <Col cols={[12, 12, 6, 6, 6]}>
          <Input
            data-testid={'input-bairro'}
            label={localPrefix.Campos.Bairro.Label}
            placeholder={localPrefix.Campos.Bairro.Placeholder}
            value={state.local.inputs.neighborhood}
            onChange={(e) => {
              localStepSetFilter({
                neighborhood: e.target.value,
              });
            }}
            loading={state.local.loadData}
            error={sent && !!validator && validator.neighborhoodIsValid().error}
            helperText={
              sent && !!validator
                ? validator.neighborhoodIsValid().helperText
                : ''
            }
            disabled={
              state.local.loadData || state.status === StepStatus.VISUALIZACAO
            }
          />
        </Col>
        <Col cols={[12, 12, 6, 6, 6]}>
          <Input
            data-testid={'input-endereco'}
            label={localPrefix.Campos.Endereco.Label}
            placeholder={localPrefix.Campos.Endereco.Placeholder}
            value={state.local.inputs.address}
            onChange={(e) => {
              localStepSetFilter({
                address: e.target.value,
              });
            }}
            loading={state.local.loadData}
            error={sent && !!validator && validator.addressIsValid().error}
            helperText={
              sent && !!validator ? validator.addressIsValid().helperText : ''
            }
            disabled={
              state.local.loadData || state.status === StepStatus.VISUALIZACAO
            }
          />
        </Col>
        <Col cols={[12, 12, 6, 6, 6]}>
          <Input
            data-testid={'input-numero'}
            label={localPrefix.Campos.Numero.Label}
            placeholder={localPrefix.Campos.Numero.Placeholder}
            value={state.local.inputs.number}
            onChange={(e) => {
              localStepSetFilter({
                number: e.target.value,
              });
            }}
            disabled={state.status === StepStatus.VISUALIZACAO}
          />
        </Col>
        <Col cols={[12, 12, 6, 6, 6]}>
          <Input
            data-testid={'input-ponto-de-referencia'}
            label={localPrefix.Campos.PontoReferencia.Label}
            placeholder={localPrefix.Campos.PontoReferencia.Placeholder}
            value={state.local.inputs.localReference}
            onChange={(e) => {
              localStepSetFilter({
                localReference: e.target.value,
              });
            }}
            disabled={state.status === StepStatus.VISUALIZACAO}
          />
        </Col>
      </Row>
      <Row>
        <Col buttonActionsCol end>
          <Button
            theme={ButtonThemes.Secondary}
            onClick={() => {
              changeState(
                RegisterContextActions.SET_SHOW_ALERT,
                JSON.stringify(state.localOld.inputs) !==
                  JSON.stringify(state.local.inputs)
              );
              if (props.reu) {
                changeState(
                  RegisterContextActions.SET_TO_PAGE,
                  `${AssessorCadastroReuniaoRoutePath}${
                    match.params.id
                      ? `${match.params.id}/etapas/entidade`
                      : `etapas/${StepIds.data}`
                  }`
                );
              } else {
                changeState(
                  RegisterContextActions.SET_TO_PAGE,
                  `${AssessorCadastroEventoRoutePath}${
                    match.params.id
                      ? `${match.params.id}/etapas/entidade`
                      : `etapas/${StepIds.data}`
                  }`
                );
              }

              if (
                JSON.stringify(state.localOld.inputs) ===
                JSON.stringify(state.local.inputs)
              ) {
                if (props.reu) {
                  history.push(
                    `${AssessorCadastroReuniaoRoutePath}${match.params.id}/etapas/entidade`
                  );
                } else {
                  history.push(
                    `${AssessorCadastroEventoRoutePath}${match.params.id}/etapas/entidade`
                  );
                }
              }
            }}
            leftIcon={<ArrowLeftIcon />}
          >
            {localPrefix.Buttons.Voltar}
          </Button>
          <Button
            data-testid={'button-save'}
            theme={ButtonThemes.Primary}
            onClick={
              state.status === StepStatus.VISUALIZACAO
                ? () => {
                    if (props.reu) {
                      history.push(
                        `${AssessorCadastroReuniaoRoutePath}${match.params.id}/etapas/pauta`
                      );
                    } else {
                      history.push(
                        `${AssessorCadastroEventoRoutePath}${match.params.id}/etapas/pauta`
                      );
                    }
                  }
                : handleSubmit
            }
            loading={loading}
            leftIcon={<CheckCircleIcon />}
            disabled={
              loading || (sent && !!validator && !validator.allIsValid())
            }
          >
            {state.status === StepStatus.VISUALIZACAO
              ? localPrefix.Buttons.Prosseguir
              : localPrefix.Buttons.Salvar}
          </Button>
        </Col>
      </Row>
    </Row>
  ) : (
    <Fragment />
  );
}
